import { Trash2, Save, Loader2, Scale, ChevronRight } from 'lucide-react';
import RecipeKatIcon from '../receptura/RecipeKatIcon';
import { getKatVisual, SECTION_COLOR, SECTION_BG } from '../receptura/receptUtils';
import ZpetTlacitko from '../ui/ZpetTlacitko';

/**
 * Hlavička detailu položky Spižírny — stejné rozvržení jako u receptu: kód,
 * název rovnou k přepsání, zařazení a jednotka jako „pilulky", přepínač aktivity
 * a Uložit s tečkou, když jsou neuložené změny.
 *
 * Proti receptu chybí výběr ikony (materiál vlastní obrázek nemá — miniatura se
 * odvozuje z kategorie) a hlavička nenese žádnou kalkulaci.
 */
export default function MaterialDetailHeader({
    detail, data, edit, setEdit, flatKat, jednotky, novyLabel,
    dirty, saving, onSave, onDelete, onBack,
    onOpenJednotky, altJednotkyPocet = 0,
    poleDirty = () => false,
    isDark, isMobile, mutedColor, secondaryColor, optionStyle, btnPrimary, btnSecondary,
    doplnkovePilulky = null,
    // Karty, které pro poznámku nemají vlastní záložku, si ji nechají vykreslit
    // rovnou v hlavičce (suroviny na desktopu). Obaly a provozní materiál ji
    // zatím mají ve svém „Základní info", takže prop nedávají.
    maPoznamku = false,
    // Na mobilu je hlavička jen navigace — editaci obsluhuje záložka „Karta".
    // Moduly, které takovou záložku nemají, prop nedávají a dostanou starou
    // editační hlavičku (obaly, provozní materiál).
    jenNavigace = false,
}) {
    const pill = {
        background: isDark ? 'rgba(255,255,255,0.04)' : 'var(--surface-base)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
    };
    const bareSelect = {
        appearance: 'none', WebkitAppearance: 'none', border: 'none', background: 'transparent',
        color: 'var(--text-primary)', fontSize: 12, fontWeight: 500, cursor: 'pointer', outline: 'none',
        paddingRight: 2, maxWidth: 160,
    };
    const dirtyRing = (on) => (on ? { boxShadow: '0 0 0 2px rgba(245,158,11,0.55)' } : {});

    const kat = flatKat.find(k => k.id === edit?.kategorie_id);
    const katCol = kat?.barva || getKatVisual(kat?.nazev).color;
    const aktivni = edit?.aktivni ?? true;

    const hranice = `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}`;

    // Kdo to je — ikona, kód, název k přepsání a pilulky. Na obou šířkách totéž,
    // jen na mobilu menší ikona (hlavička tam nescrolluje pryč, takže se počítá
    // každý pixel výšky).
    const telo = (
        <div className="flex items-start gap-3 min-w-0 flex-1">
            {detail !== 'new' && data && (
                <RecipeKatIcon ikona={kat?.ikona} kategorieNazev={kat?.nazev || data.nazev} kategorieBarva={kat?.barva}
                    size={isMobile ? 42 : 58} radius={isMobile ? 8 : 9} />
            )}
            <div className="min-w-0 flex-1">
                {/* Řádek: kód + název */}
                <div className="flex items-center gap-2 flex-wrap">
                    {detail !== 'new' && data && (
                        <span className="px-2 py-0.5 rounded-full text-[11px] font-bold" style={{ background: SECTION_BG, color: SECTION_COLOR, flexShrink: 0 }}>{data.kod}</span>
                    )}
                    <input value={edit?.nazev || ''} onChange={e => setEdit({ ...edit, nazev: e.target.value })}
                        placeholder={novyLabel}
                        style={{ fontSize: isMobile ? 15 : 16, fontWeight: 700, border: 'none', background: 'transparent', color: 'var(--text-primary)', flex: '1 1 180px', minWidth: 120, outline: 'none', padding: '2px 4px', borderRadius: 5, ...dirtyRing(poleDirty('nazev')) }} />
                </div>
                {/* Řádek 2: kategorie + jednotka (+ typové doplňky) */}
                <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                    <div className="inline-flex items-center gap-1.5 rounded-full py-1 pl-2 pr-1.5" style={{ ...pill, ...dirtyRing(poleDirty('kategorie_id')) }}>
                        <span style={{ width: 8, height: 8, borderRadius: 2, flexShrink: 0, background: edit?.kategorie_id ? katCol : 'transparent', border: edit?.kategorie_id ? 'none' : `1px solid ${mutedColor}` }} />
                        <select value={edit?.kategorie_id || ''} onChange={e => setEdit({ ...edit, kategorie_id: e.target.value ? parseInt(e.target.value) : null })} style={bareSelect}>
                            <option value="" style={optionStyle}>Bez kategorie</option>
                            {flatKat.map(k => <option key={k.id} value={k.id} style={optionStyle}>{'— '.repeat(k._depth)}{k.nazev}</option>)}
                        </select>
                    </div>
                    {/* Jednotky řeší modal (základní + sekundární) — stejně jako u receptu */}
                    <button onClick={onOpenJednotky} title="Nastavit jednotky"
                        className="inline-flex items-center gap-1.5 rounded-full py-1 pl-2 pr-2.5"
                        style={{ background: pill.background, border: `1px solid ${!edit?.jednotka_id ? 'rgba(239,68,68,0.5)' : (isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)')}`, cursor: 'pointer', ...dirtyRing(poleDirty('jednotka_id')) }}>
                        <Scale size={12} style={{ color: !edit?.jednotka_id ? '#EF4444' : mutedColor, flexShrink: 0 }} />
                        <span className="text-xs font-medium" style={{ color: edit?.jednotka_id ? 'var(--text-primary)' : '#EF4444' }}>
                            {edit?.jednotka_id ? (jednotky.find(j => j.id === edit.jednotka_id)?.zkratka || 'jedn.') : 'Jednotka *'}
                            {altJednotkyPocet > 0 && <span style={{ color: mutedColor, fontWeight: 400 }}> +{altJednotkyPocet}</span>}
                        </span>
                    </button>
                    {doplnkovePilulky}
                </div>

                {/* Poznámka bydlela v záložce „Základní info", která kromě ní nesla
                    jen souhrn duplikující stálý panel vpravo. Je to jedno pole a patří
                    k identitě položky stejně jako název — tedy do hlavičky, kde ho
                    ukládá totéž tlačítko Uložit. Bez rámečku, ať to není pole ve formuláři. */}
                {maPoznamku && (
                    <input value={edit?.poznamka || ''} onChange={e => setEdit({ ...edit, poznamka: e.target.value })}
                        placeholder="Poznámka…"
                        className="text-xs mt-1.5"
                        style={{
                            width: '100%', border: 'none', background: 'transparent', color: secondaryColor,
                            outline: 'none', padding: '3px 4px', borderRadius: 5, minHeight: isMobile ? 28 : undefined,
                            ...dirtyRing(poleDirty('poznamka')),
                        }} />
                )}
            </div>
        </div>
    );

    const akce = (
        <>
            <button onClick={() => setEdit({ ...edit, aktivni: !aktivni })} title={aktivni ? 'Aktivní — klikni pro deaktivaci' : 'Neaktivní (v archivu)'}
                className="flex items-center gap-1.5" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', marginRight: 2 }}>
                <span style={{ width: 34, height: 20, borderRadius: 999, flexShrink: 0, position: 'relative', transition: 'background 0.2s', background: aktivni ? '#22C55E' : (isDark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.18)'), ...dirtyRing(poleDirty('aktivni')) }}>
                    <span style={{ position: 'absolute', top: 2, left: aktivni ? 16 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 2px rgba(0,0,0,0.3)' }} />
                </span>
                {!isMobile && <span className="text-[11px] font-medium" style={{ color: aktivni ? '#22C55E' : mutedColor }}>{aktivni ? 'Aktivní' : 'Neaktivní'}</span>}
            </button>
            {/* Smazat na mobilu ne. Není to editace, je to zničení záznamu —
                a sedělo by pár pixelů od Uložit na zařízení, kde se překliká
                snáz než myší. Mazání patří na desktop k plné editaci. */}
            {detail !== 'new' && !isMobile && (
                <button onClick={onDelete} style={{ ...btnSecondary, padding: '6px 10px', color: '#EF4444' }}>
                    <Trash2 size={13} /> Smazat
                </button>
            )}
            <button onClick={onSave} disabled={saving || (!dirty && detail !== 'new')}
                style={{
                    ...btnPrimary, padding: isMobile ? '0 18px' : '6px 14px',
                    minHeight: isMobile ? 40 : undefined,
                    position: 'relative', opacity: (saving || (!dirty && detail !== 'new')) ? 0.55 : 1,
                }}>
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Uložit
                {dirty && detail !== 'new' && (
                    <span style={{ position: 'absolute', top: -3, right: -3, width: 10, height: 10, borderRadius: '50%', background: '#F59E0B', border: `2px solid ${isDark ? '#1a1a2e' : '#fff'}` }} />
                )}
            </button>
        </>
    );

    /* Mobil, když si kartu obsluhuje záložka „Karta“ (`jenNavigace`): hlavička
       přestává být formulářem. Měla v sobě deset ovladačů na 190 px z 844 —
       navigaci, přepínač stavu bez popisku a potvrzení v jedné řadě, pod tím
       výběr kategorie vysoký 18 px a zaškrtávátko 13×13. Šest z deseti cílů bylo
       pod dotykovým minimem normy.

       Zůstává jen to, co hlavička opravdu má říct: kdo to je a jak ven nebo
       uložit. Ostatní data jsou pod ní **textem**, ne ovladači — editují se
       v záložce Karta, kde má každý řádek 44 px. Aktivita je v tom textu jako
       odznak: přepínač se odstěhoval, ale stav musí být vidět dál. */
    if (isMobile && jenNavigace) {
        const jedn = jednotky.find(j => j.id === edit?.jednotka_id)?.zkratka;
        const meta = [
            detail !== 'new' ? data?.kod : null,
            kat?.nazev ?? 'Bez kategorie',
            jedn ? `${jedn}${altJednotkyPocet > 0 ? ` +${altJednotkyPocet}` : ''}` : null,
        ].filter(Boolean).join(' · ');
        return (
            <div className="flex flex-col" style={{ padding: '8px 12px 10px', gap: 6, flexShrink: 0, borderBottom: hranice }}>
                <div className="flex items-center gap-2">
                    {onBack && (
                        <ZpetTlacitko onClick={onBack} popis="Zpět na seznam" />
                    )}
                    <span className="min-w-0 flex-1 truncate text-[15px] font-bold" style={{ color: 'var(--text-primary)' }}>
                        {edit?.nazev?.trim() || novyLabel}
                    </span>
                    <button onClick={onSave} disabled={saving || (!dirty && detail !== 'new')}
                        style={{
                            ...btnPrimary, padding: '0 16px', minHeight: 40, flexShrink: 0,
                            position: 'relative', opacity: (saving || (!dirty && detail !== 'new')) ? 0.55 : 1,
                        }}>
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Uložit
                        {dirty && detail !== 'new' && (
                            <span style={{ position: 'absolute', top: -3, right: -3, width: 10, height: 10, borderRadius: '50%', background: '#F59E0B', border: `2px solid ${isDark ? '#181620' : '#fff'}` }} />
                        )}
                    </button>
                </div>
                <div className="flex items-center gap-1.5 text-[11px] min-w-0" style={{ color: mutedColor }}>
                    {kat && <span style={{ width: 8, height: 8, borderRadius: 2, flexShrink: 0, background: katCol }} />}
                    <span className="truncate">{meta}</span>
                    {!aktivni && (
                        <span className="px-1.5 py-0.5 rounded flex-shrink-0 font-semibold"
                            style={{ background: 'rgba(148,163,184,0.18)', color: secondaryColor }}>Neaktivní</span>
                    )}
                </div>
            </div>
        );
    }

    // Mobil: „Zpět" na seznam je jediná cesta z detailu ven, takže má vlastní
    // řádek úplně nahoře a vypadá jako tlačítko, ne jako odkaz. Vedle něj akce —
    // Uložit musí být vidět, aniž by se muselo scrollovat.
    if (isMobile) {
        return (
            <div className="flex flex-col" style={{ padding: '8px 12px 10px', gap: 8, flexShrink: 0, borderBottom: hranice }}>
                <div className="flex items-center gap-2">
                    {onBack && (
                        <ZpetTlacitko onClick={onBack} popis="Zpět na seznam" />
                    )}
                    <div className="flex items-center gap-1.5" style={{ marginLeft: 'auto', flexShrink: 0 }}>{akce}</div>
                </div>
                {telo}
            </div>
        );
    }

    return (
        <div className="flex items-start justify-between p-4" style={{ borderBottom: hranice }}>
            {telo}
            <div className="flex items-center gap-2" style={{ flexShrink: 0 }}>{akce}</div>
        </div>
    );
}
